/**
 * One row in a list.
 *
 * Thumbnails resolve asynchronously — generated covers are drawn in the
 * browser, YouTube thumbnails come over the network — so the artwork slot
 * starts as a glyph and swaps itself in when it is ready.
 */

import type { JSX } from '@solidjs/web';
import { type Component, createMemo, createSignal, onSettled, Show } from 'solid-js';
import { thumbUrl } from '../../data/artwork.ts';
import type { Track } from '../../types/media.ts';
import styles from './Row.module.css';

export interface RowProps {
  title: string;
  subtitle?: string;
  /** Shown in the artwork slot until a thumbnail arrives. */
  glyph: JSX.Element;
  /** A direct thumbnail URL, when one is already known. */
  artworkUrl?: string | null;
  /** Resolved asynchronously when given — generated demo covers. */
  track?: Track;
  tail?: JSX.Element;
  playing?: boolean;
  onSelect: () => void;
}

/** The three dancing bars that mark the record on the platter. */
export const Equaliser: Component = () => (
  <span class={styles.eq} data-testid="equaliser">
    <i />
    <i />
    <i />
  </span>
);

export const Row: Component<RowProps> = (props) => {
  const [resolved, setResolved] = createSignal<string | null>(null);

  /* Hoisted: reading a prop for the first time inside onSettled would create
     its memo in a scope that forbids new reactive primitives. */
  const given = createMemo(() => props.artworkUrl ?? null);
  const source = createMemo(() => props.track);

  onSettled(() => {
    const t = source();
    if (!t || given()) return;
    let live = true;
    void thumbUrl(t).then((url) => {
      if (live) setResolved(url);
    });
    return () => {
      live = false;
    };
  });

  const url = (): string | null => given() ?? resolved();

  return (
    <button
      type="button"
      data-testid="row"
      data-playing={props.playing ? 'true' : undefined}
      class={[styles.row, { [styles.playing as string]: !!props.playing }]}
      onClick={() => props.onSelect()}
    >
      <span class={styles.art} data-testid="row-art">
        <Show when={url()} fallback={<span class={styles.glyph}>{props.glyph}</span>}>
          {(src) => <img alt="" loading="lazy" referrerpolicy="no-referrer" src={src()} />}
        </Show>
      </span>

      <span class={styles.body}>
        <span class={styles.title} data-testid="row-title">
          {props.title}
        </span>
        <Show when={props.subtitle}>
          <span class={styles.sub} data-testid="row-sub">
            {props.subtitle}
          </span>
        </Show>
      </span>

      <span class={styles.tail} data-testid="row-tail">
        {props.tail}
      </span>
    </button>
  );
};
